import clsx from "clsx";
import {
	VideoEventLogStatusType,
	VideoIngestorVideoStatus,
	VideoProviderName,
	VideoStatusType,
} from "libs/apiClient";
import { VideoApiStatus, VideoStatus, VideoUploadOrigin } from "./metadata/types";
import type { Preset } from "./tables/columns";

/** Mirrors the `Assert\Length(max: 255)` guard on the backend VideoRenameRequest. */
export const VIDEO_NAME_MAX_LENGTH = 255;

export function formatDate(dateString?: string | null) {
	if (dateString) {
		const date = new Date(dateString);

		return new Intl.DateTimeFormat("en-UK", {
			year: "numeric",
			day: "numeric",
			month: "numeric",
		}).format(date);
	}
}

export function formatDateTime(dateString?: string | null) {
	if (dateString) {
		const date = new Date(dateString);

		return new Intl.DateTimeFormat("en-UK", {
			year: "numeric",
			day: "numeric",
			month: "numeric",
			hour: "numeric",
			minute: "numeric",
			second: "numeric",
		}).format(date);
	}
}

export function formatDuration(durationString?: string | null) {
	if (durationString) {
		const [hours, minutes, seconds] = durationString.split(":");
		const roundedSeconds = Math.round(Number.parseFloat(seconds));

		return `${hours}:${minutes}:${roundedSeconds}`;
	}
}

export function roundSizeString(input?: string | null) {
	if (input) {
		const numericPart = Number.parseFloat(input);
		const roundedNumericPart = numericPart.toFixed(2);
		const result = input.replace(/[\d.]+/, roundedNumericPart);

		return result;
	}
}

/** Origins whose label is not simply the capitalised raw value. */
const originLabels = new Map<string, string>([
	[VideoUploadOrigin.Manual, "Manual Upload"],
	[VideoUploadOrigin.Api, "API"],
	[VideoUploadOrigin.Discover, "Discover"],
]);

/** Providers whose label needs more than hyphen splitting to read correctly. */
const providerLabels = new Map<string, string>([
	[VideoProviderName.Values.veo, "Veo"],
	[VideoProviderName.Values["die-ligen"], "Die Ligen"],
]);

/** Provider values that carry no information worth showing next to the origin. */
const uninformativeProviders = new Set([VideoProviderName.Values.unknown, ""]);

function capitalizeWords(value: string) {
	return value
		.split(/[-_\s]+/)
		.filter((word) => word !== "")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

/**
 * Source shown in the video tables, combining the video server `origin` and `provider`,
 * e.g. ("api", "veo") reads "API - Veo" and ("manual", "unknown") reads "Manual Upload".
 */
export function formatSource(origin?: string | null, provider?: string | null) {
	if (origin == null || origin === "") {
		return undefined;
	}

	const originLabel = originLabels.get(origin) ?? capitalizeWords(origin);

	if (provider == null || uninformativeProviders.has(provider)) {
		return originLabel;
	}

	return `${originLabel} - ${providerLabels.get(provider) ?? capitalizeWords(provider)}`;
}

/** Formats a status for display, e.g. "waiting_for_stitching" reads "Waiting for stitching". */
export function formatApiStatus(apiStatus?: string | null) {
	if (apiStatus == null || apiStatus === "") {
		return undefined;
	}

	const words = apiStatus.replace(/_/g, " ").toLowerCase();

	return words.charAt(0).toUpperCase() + words.slice(1);
}

export function getProviderMetadataField(
	providerMetadata: string | Record<string, unknown> | null | undefined,
	field: string,
) {
	if (providerMetadata == null || providerMetadata === "") {
		return undefined;
	}

	let parsed: unknown = providerMetadata;
	if (typeof providerMetadata === "string") {
		try {
			parsed = JSON.parse(providerMetadata);
		} catch {
			return undefined;
		}
	}

	if (typeof parsed !== "object" || parsed === null) {
		return undefined;
	}

	const value = (parsed as Record<string, unknown>)[field];

	return typeof value === "string" ? value : undefined;
}

export function getStatusClass(status?: string) {
	if (status) {
		return clsx("statusCell", {
			red: status.includes("ERROR"),
			green: status.includes("OK") || status.includes("FINISHED"),
			gray: status === VideoStatus.Uploaded,
		});
	}
	return "-";
}

/** The uploader list only feeds the Uploaded By filter, so it is fetched for the tabs that show one. */
export function enableUploaders(preset: Preset) {
	return preset === "waiting-for-approval" || preset === "approved" || preset === "";
}

const fileReadyApiStatuses = new Set([
	VideoIngestorVideoStatus.Values.downloaded,
	VideoIngestorVideoStatus.Values.optimized,
	VideoIngestorVideoStatus.Values.migrated,
]);

export function isFileReady(apiStatus?: string | null) {
	if (apiStatus == null || apiStatus === "") {
		return true;
	}

	return fileReadyApiStatuses.has(apiStatus.toLowerCase());
}

export function fileNotReadyReason(apiStatus?: string | null) {
	return `Unavailable while the API status is "${formatApiStatus(apiStatus) ?? "unknown"}"`;
}

interface VideoDeletionSignals {
	deletedAt?: string | null;
	deletedBy?: string | null;
	status?: string | null;
	apiStatus?: string | null;
	lastEvent?: string | null;
}

/**
 * Soft-deleted uploads. The detail endpoint does not always return `deletedAt` even when the
 * Deleted tab lists the video, so also treat status / apiStatus / lastEvent as deletion signals.
 */
export function isVideoDeleted(video: VideoDeletionSignals) {
	if (video.deletedAt != null && video.deletedAt !== "") {
		return true;
	}

	if (video.deletedBy != null && video.deletedBy !== "") {
		return true;
	}

	if (video.status === VideoStatusType.Values.VIDEO_DELETED) {
		return true;
	}

	if (video.apiStatus != null && video.apiStatus.toLowerCase() === VideoApiStatus.Deleted) {
		return true;
	}

	if (
		video.lastEvent === VideoEventLogStatusType.Values.VIDEO_DELETED ||
		video.lastEvent === VideoEventLogStatusType.Values.UPLOAD_DELETED
	) {
		return true;
	}

	return false;
}

interface VideoPlaybackLog {
	status?: string | null;
	message?: string | null;
}

export interface VideoPlaybackSignals extends VideoDeletionSignals {
	link?: string | null;
	logs?: VideoPlaybackLog[];
}

/** How long a visible player may buffer before a silent 404 is treated as unavailable. */
export const PLAYBACK_READY_TIMEOUT_MS = 8_000;

interface PlaybackReadyTimeoutParams {
	isSetupTabVisible: boolean;
	streamUnavailable: boolean;
	playbackFailed: boolean;
	playbackReady: boolean;
	playbackSource: string;
}

/**
 * Vidstack defaults to load="visible", so a hidden Setup tab never emits can-play.
 * Only start the ready timeout while the player is actually on screen and still waiting.
 */
export function shouldStartPlaybackReadyTimeout({
	isSetupTabVisible,
	streamUnavailable,
	playbackFailed,
	playbackReady,
	playbackSource,
}: PlaybackReadyTimeoutParams) {
	if (isSetupTabVisible === false) {
		return false;
	}

	if (streamUnavailable || playbackFailed || playbackReady) {
		return false;
	}

	return playbackSource !== "";
}

const FILE_MISSING_MESSAGE = "not found";

const playbackSuccessStatuses = new Set<string>([
	VideoEventLogStatusType.Values.OUTPUT_UPLOAD_OK,
	VideoEventLogStatusType.Values.PARTIAL_OUTPUT_UPLOAD_OK,
	VideoEventLogStatusType.Values.PROCESSING_FINISHED,
	VideoEventLogStatusType.Values.HLS_CREATION_OK,
	VideoEventLogStatusType.Values.INGEST_OK,
]);

const playbackFileMissingStatuses = new Set<string>([
	VideoEventLogStatusType.Values.PROCESSING_ERROR,
	VideoEventLogStatusType.Values.INGEST_ERROR,
]);

/**
 * Newest-first logs: the latest decisive event is a processing/ingest error whose
 * message says the file is gone. A later success (OUTPUT_UPLOAD_OK, INGEST_OK, …)
 * means the stream may still play.
 */
export function isVideoFileMissingFromLogs(logs?: VideoPlaybackLog[]) {
	if (logs == null || logs.length === 0) {
		return false;
	}

	for (const log of logs) {
		const status = log.status;
		if (status == null || status === "") {
			continue;
		}

		if (playbackSuccessStatuses.has(status)) {
			return false;
		}

		if (playbackFileMissingStatuses.has(status)) {
			const message = log.message ?? "";
			if (message.toLowerCase().includes(FILE_MISSING_MESSAGE)) {
				return true;
			}
		}
	}

	return false;
}

/**
 * The detail endpoint always synthesizes `link` from id + token, so a URL is not
 * proof the file exists. Hide the player when the upload is deleted, the URL is
 * empty, or logs show the source file was lost.
 */
export function isVideoPlaybackUnavailable(video: VideoPlaybackSignals) {
	if (isVideoDeleted(video)) {
		return true;
	}

	if (video.link == null || video.link === "") {
		return true;
	}

	return isVideoFileMissingFromLogs(video.logs);
}
